import React from "react";
import cn from "./helpers/cn";
import Typography from "./Typography";
import { ListItemContext, } from "./ListItem";

// Shape mínimo das props aceitas para sobrescrever Typography em primary/secondary.
type TypoExtraProps = Record<string, unknown>;

interface ListItemTextProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  component?: React.ElementType;
  primary?: React.ReactNode;
  secondary?: React.ReactNode;
  /** Adiciona padding-left para alinhar com texto após ListItemIcon. */
  inset?: boolean;
  /** Quando true, não envolve `primary`/`secondary` em Typography. */
  disableTypography?: boolean;
  primaryTypographyProps?: TypoExtraProps;
  secondaryTypographyProps?: TypoExtraProps;
  children?: React.ReactNode;
}

const ListItemText = React.forwardRef<HTMLDivElement, ListItemTextProps>((props, ref) => {
  const itemCtx = React.useContext(ListItemContext);

  const {
    className,
    component: Component = "div",
    primary: primaryProp,
    secondary: secondaryProp,
    inset = false,
    disableTypography = false,
    primaryTypographyProps,
    secondaryTypographyProps,
    children,
    ...other
  } = props;

  // Igual ao MUI: se `primary` não vier explicitamente, usa `children`.
  const primary = primaryProp ?? children;
  const secondary = secondaryProp;

  const dense = itemCtx.dense;

  return (
    <Component
      ref={ref}
      className={cn(
        // MUI v4: flex 1 1 auto, margens verticais 4px (6px quando dense=false).
        "flex-1 min-w-0",
        dense ? "my-1" : "my-1.5",
        inset && "pl-[56px]",
        className
      )}
      {...other}
    >
      {primary !== null && primary !== undefined && (
        disableTypography || React.isValidElement(primary)
          ? primary
          : <Typography
            variant={dense ? "body2" : "body1"}
            component="span"
            display="block"
            {...(primaryTypographyProps as Record<string, unknown>)}
          >
            {primary}
          </Typography>
      )}

      {secondary !== null && secondary !== undefined && (
        disableTypography || React.isValidElement(secondary)
          ? secondary
          : <Typography
            variant="body2"
            color="textSecondary"
            component="p"
            display="block"
            {...(secondaryTypographyProps as Record<string, unknown>)}
          >
            {secondary}
          </Typography>
      )}
    </Component>
  );
});

ListItemText.displayName = "ListItemText";

export default ListItemText;
